import React from "react";
import {
    Card,
    Popper,
    Theme,
    Typography,
    darken,
    lighten,
    styled,
    useTheme,
    recomposeColor,
    decomposeColor,
} from "@mui/material";

import { ResizeComponent, SizeProps } from "@iva/resize-component";

import { CollapseableColumn, ParameterizedColumn } from "#/util/columnSorting";
import { interpolateRgbBasisClosed } from "d3-interpolate";
import {
    color,
    interpolateHsl,
    interpolateLab,
    interpolateRgb,
    piecewise,
    range,
    scaleDiverging,
    scaleLinear,
    scaleSequential,
} from "d3";

/**
 * Declarations
 */

export interface Props {
    columnOrder: CollapseableColumn[];
}

export interface DoiMapProps {
    columnDoi: number[][];
    clusterDoi: ClusterData[];
    columnAttributes: ParameterizedColumn[][];
    columnOrder: CollapseableColumn[];
    spacing: number;
    maxValue?: number;
    minValue?: number;
    columnHoverCallback?: ColumnCallback;
}

type MetricType = "relevance" | "query" | "comparison" | "favorite";

export interface SingleTermMapProps {
    columnAttributes: ParameterizedColumn[][];
    columnOrder: CollapseableColumn[];
    measure: MetricType;
    spacing: number;
    maxValue?: number;
    minValue?: number;
    columnHoverCallback?: ColumnCallback;
}

interface SvgProps {
    elements: VisAttributes[];
    height: number;
    width: number;
    cellSize: number;
    spacing: number;
    hover?: [number, number];
    svgRef?: React.MutableRefObject<SVGSVGElement | null>;
    max?: number;
    min?: number;
    elementHoverCallback?: ElementCallback;
}

interface TooltipProps {
    columnOrder: CollapseableColumn[];
    attributes: ParameterizedColumn[][];
    rowId: number;
    colId: number;
    clusters?: ClusterData[];
    measure?: MetricType;
}

interface CellTooltipProps {
    columnOrder: CollapseableColumn[];
    attributes: ParameterizedColumn[][];
    rowId: number;
    colId: number;
    measure?: MetricType;
}

interface ClusterTooltipProps {
    name: string;
    numCols: number;
    doi: number;
}

type ColumnCallback = (columns: string[]) => void;
type ElementCallback = (col: number, row: number) => void;

export interface ClusterData {
    start: number;
    count: number;
    doi: number;
}

interface VisAttributes {
    x: number;
    y: number;
    width: number;
    count: number;
    columnName: string | string[];
    id: number;
    tableId: number;
}

const HoverPopper = styled(Popper)({
    zIndex: 1400,
});

const TooltipCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
}));

/**
 * Helper Functions
 */

/**
 * Computes a muted version of the given color by the given fraction.
 * Lightens/Darkens the color depending on the given theme's mode.
 * @param fraction the mute fraction in range [0, 1]
 * @param theme the theme
 * @param color the color variant, can be either "primary" or "secondary"
 * @returns the muted color
 */
function getColor(
    fraction: number,
    theme: Theme,
    color: "primary" | "secondary"
): string {
    const baseColor =
        color === "primary"
            ? theme.palette.primary.main
            : theme.palette.secondary.main;
    if (theme.palette.mode === "light") {
        const coefficient = (1 - fraction) * 0.8;
        return lighten(baseColor, coefficient);
    } else {
        const coefficient = (1 - fraction) * 0.6;
        return darken(baseColor, coefficient);
    }
}

export function getInterpolatedColor(
    position: number,
    colors: string[]
): string {
    if (position < 0 || position > 1) {
        throw new Error("color position must be within 0..1");
    }
    if (colors.length < 2) {
        throw new Error("Need at least 2 colors for interpolation");
    }
    return piecewise(interpolateLab, colors)(position);
    //
    // // colors in array are distributed equidistantly over 0..1.
    // const width = 1 / (colors.length - 1);
    // const scaledPosition = (colors.length - 1) * position;
    // const left = Math.floor(scaledPosition);
    // const right = Math.ceil(scaledPosition);
    // const l = colors[left];
    // const r = colors[right];
    // console.log(left, "<", scaledPosition, "<", right);
    // const frac = position - width * left;
    // console.log(`${frac} = ${position} - ${width} * ${left}`)
    // // console.log(l, "<", frac, "<", r, "||", position);
    // const finalColor = interpolateRgbBasisClosed([l, r])(scaledPosition-left);
    // // console.log("col:", l, r, position);
    // return finalColor;
}

/**
 * Component
 */

/**
 * This creates the content for a cell tooltip
 */
const CellTooltip: React.FC<CellTooltipProps> = props => {
    const colName = props.columnOrder[props.colId].name;
    const cellAttributes = props.attributes[props.rowId].find(
        d => d.name === colName
    );

    let doi =
        cellAttributes !== undefined
            ? cellAttributes.relevance +
              cellAttributes.comparison +
              cellAttributes.query +
              cellAttributes.favorite
            : undefined;

    let label: string | undefined = undefined;
    let value: number | undefined = undefined;
    if (cellAttributes !== undefined) {
        switch (props.measure) {
            case "relevance":
                label = "Relevance";
                value = cellAttributes.relevance;
                break;
            case "comparison":
                label = "Comparison";
                value = cellAttributes.comparison;
                break;
            case "query":
                label = "Query";
                value = cellAttributes.query;
                break;
            case "favorite":
                label = "Favorite";
                value = cellAttributes.favorite;
        }
    }

    return (
        <TooltipCard elevation={2}>
            <Typography variant="body1">{colName}</Typography>
            <Typography variant="caption">
                {doi !== undefined
                    ? "DOI in table " +
                      props.rowId +
                      ": " +
                      Math.round(doi * 100) / 100
                    : "Does not exist in table " + props.rowId}
            </Typography>
            {label !== undefined && value !== undefined ? (
                <Typography variant="caption">
                    {label +
                        " in table " +
                        props.rowId +
                        ": " +
                        Math.round(value * 100) / 100}
                </Typography>
            ) : null}
        </TooltipCard>
    );
};

/**
 * This creates the content for a cluster tooltip
 */
const ClusterTooltip: React.FC<ClusterTooltipProps> = props => {
    return (
        <TooltipCard elevation={2}>
            <Typography variant="body1">{props.name}</Typography>
            <Typography variant="caption">
                {"Number of Columns: " + props.numCols}
                <br />
                {"Aggregated DOI: " + Math.round(props.doi * 100) / 100}
            </Typography>
        </TooltipCard>
    );
};

/**
 * This creates content for a map tooltip
 */
const MapTooltip: React.FC<TooltipProps> = ({
    columnOrder,
    attributes,
    rowId,
    colId,
    clusters,
    measure,
}) => {
    // out of bounds checks
    if (rowId < 0 || colId < 0 || colId > columnOrder.length - 1) {
        return null;
    }
    if (
        (clusters !== undefined && rowId > attributes.length) ||
        (clusters === undefined && rowId > attributes.length - 1)
    ) {
        return null;
    }

    if (clusters !== undefined) {
        if (rowId === 0) {
            // find hovered cluster
            const clusterId = clusters.findIndex(
                d => d.start <= colId && d.start + d.count > colId
            );
            if (clusterId !== -1) {
                return (
                    <ClusterTooltip
                        name={"Cluster " + clusterId}
                        numCols={clusters[clusterId].count}
                        doi={clusters[clusterId].doi}
                    />
                );
            } else {
                return null;
            }
        } else {
            // find hovered column instance
            const realRowId = rowId - 1;
            return (
                <CellTooltip
                    columnOrder={columnOrder}
                    attributes={attributes}
                    colId={colId}
                    rowId={realRowId}
                    measure={measure}
                />
            );
        }
    } else {
        // find hovered column instance
        return (
            <CellTooltip
                columnOrder={columnOrder}
                attributes={attributes}
                colId={colId}
                rowId={rowId}
                measure={measure}
            />
        );
    }
};

/**
 * This draws the actual SVG with the given width, height, and elements
 */
const DoiMapSvg: React.FC<SvgProps> = props => {
    const theme = useTheme();

    // compute heatmap domain if not given
    const max = props.max ?? Math.max(...props.elements.map(d => d.count));
    const min = props.min ?? Math.min(...props.elements.map(d => d.count));
    const range = max === min ? 1 : max - min;

    function handleMouseMove(evt: React.MouseEvent) {
        const mouseX = evt.nativeEvent.offsetX;
        const mouseY = evt.nativeEvent.offsetY;

        const col = Math.floor(
            (mouseX + props.spacing * 0.5) / (props.cellSize + props.spacing)
        );
        const row = Math.floor(
            (mouseY + props.spacing * 0.5) / (props.cellSize + props.spacing)
        );
        (props.elementHoverCallback as ElementCallback)(col, row);
    }

    // create geometry of hover crosshair
    let hoverHighlight: JSX.Element | null = null;
    if (props.hover !== undefined) {
        let hoverStartX = props.hover[0] * (props.cellSize + props.spacing);
        if (props.hover[0] > 0) {
            hoverStartX -= 0.5 * props.spacing;
        }
        let hoverStartY = props.hover[1] * (props.cellSize + props.spacing);
        if (props.hover[1] > 0) {
            hoverStartY -= 0.5 * props.spacing;
        }
        let hoverWidthX = props.cellSize + props.spacing;
        if (props.hover[0] === 0) {
            hoverWidthX -= 0.5 * props.spacing;
        }
        let hoverWidthY = props.cellSize + props.spacing;
        if (props.hover[1] === 0) {
            hoverWidthY -= 0.5 * props.spacing;
        }
        hoverHighlight = (
            <>
                {/* -- vertical highlight -- */}
                <rect
                    x={hoverStartX}
                    y={0}
                    width={hoverWidthX}
                    height={props.height}
                    fill={theme.palette.action.focus}
                />
                {/* -- horizontal highlight (up to intersection with vertical) -- */}
                <rect
                    x={0}
                    y={hoverStartY}
                    width={hoverStartX}
                    height={hoverWidthY}
                    fill={theme.palette.action.focus}
                />
                {/* -- horizontal highlight (after intersection with vertical) -- */}
                <rect
                    x={hoverStartX + hoverWidthX}
                    y={hoverStartY}
                    width={props.width - (hoverStartX + hoverWidthX)}
                    height={hoverWidthY}
                    fill={theme.palette.action.focus}
                />
            </>
        );
    }

    // output
    return (
        <svg
            ref={props.svgRef}
            width={props.width}
            height={props.height}
            style={{ marginTop: theme.spacing(1) }}
            onMouseMove={
                props.elementHoverCallback ? handleMouseMove : undefined
            }
            onMouseLeave={
                props.elementHoverCallback
                    ? () =>
                          (props.elementHoverCallback as ElementCallback)(
                              -1,
                              -1
                          )
                    : undefined
            }
        >
            {hoverHighlight}
            {props.elements.map(d => (
                <rect
                    key={"element_" + d.id}
                    x={0}
                    y={0}
                    width={d.width}
                    height={props.cellSize}
                    fill={getInterpolatedColor((d.count - min) / range, [
                        recomposeColor(
                            decomposeColor(theme.palette.secondary.main)
                        ),
                        recomposeColor(
                            decomposeColor(theme.palette.grey["50"])
                        ),
                        recomposeColor(
                            decomposeColor(theme.palette.primary.main)
                        ),
                    ])}
                    style={{
                        transform: "translate(" + d.x + "px," + d.y + "px)",
                        transition: theme.transitions.create("transform", {
                            duration: theme.transitions.duration.shortest,
                        }),
                    }}
                />
            ))}
        </svg>
    );
};

/**
 * This creates a minimap for the given one of the DOI terms
 */
const DoiMapSubInternal: React.FC<SingleTermMapProps & SizeProps> = props => {
    const svgRef = React.useRef<SVGSVGElement | null>(null);
    const [hoverRow, setHoverRow] = React.useState(-1);
    const [hoverCol, setHoverCol] = React.useState(-1);

    // extract measure for all columns
    const values = React.useMemo(() => {
        return props.columnAttributes.map(table =>
            table.map(col => {
                switch (props.measure) {
                    case "relevance":
                        return col.relevance;
                    case "query":
                        return col.query;
                    case "comparison":
                        return col.comparison;
                    case "favorite":
                        return col.favorite;
                }
            })
        );
    }, [props.columnAttributes]);

    // compute visual parameters
    const cellSize =
        (props.size.width - (props.columnOrder.length - 1) * props.spacing) /
        props.columnOrder.length;
    const canvasHeight =
        cellSize * props.columnAttributes.length +
        props.spacing * props.columnAttributes.length;

    // construct visual column elements
    const elements: VisAttributes[] = props.columnAttributes
        .flatMap((table, tableId) => {
            return props.columnOrder.map((col, colId) => {
                const idx = table.findIndex(d => d.name === col.name);
                if (idx !== -1) {
                    return {
                        x: (props.spacing + cellSize) * colId,
                        y: (props.spacing + cellSize) * tableId,
                        width: cellSize,
                        count: values[tableId][idx],
                        columnName: col.name,
                        id: colId + (tableId + 1) * props.columnOrder.length,
                        tableId: tableId,
                    };
                } else {
                    return null;
                }
            });
        })
        .filter(d => d !== null) as VisAttributes[];

    function handleHover(col: number, row: number) {
        if (col === -1) {
            (props.columnHoverCallback as ColumnCallback)([]);
            setHoverCol(-1);
            setHoverRow(-1);
        } else if (row > -1) {
            (props.columnHoverCallback as ColumnCallback)([
                props.columnOrder[col].name,
            ]);
            setHoverCol(col);
            setHoverRow(row);
        }
    }

    const tooltipContent = (
        <MapTooltip
            columnOrder={props.columnOrder}
            attributes={props.columnAttributes}
            rowId={hoverRow}
            colId={hoverCol}
            measure={props.measure}
        />
    );
    const tooltipOpen = tooltipContent !== null;

    // output
    return (
        <>
            <HoverPopper
                open={tooltipOpen}
                anchorEl={tooltipOpen ? svgRef.current : undefined}
                placement="top"
            >
                {tooltipContent}
            </HoverPopper>
            <DoiMapSvg
                svgRef={svgRef}
                width={props.size.width}
                height={canvasHeight}
                cellSize={cellSize}
                spacing={props.spacing}
                elements={elements}
                max={props.maxValue}
                min={props.minValue}
                hover={
                    hoverCol !== -1 && hoverRow !== -1
                        ? [hoverCol, hoverRow]
                        : undefined
                }
                elementHoverCallback={
                    props.columnHoverCallback !== undefined
                        ? handleHover
                        : undefined
                }
            />
        </>
    );
};

export const DoiMapSub = ResizeComponent(DoiMapSubInternal);

/**
 * This creates the main DOI map, for the aggregated DOI score and the clusters
 */
export const DoiMapMain = ResizeComponent((props: DoiMapProps & SizeProps) => {
    const svgRef = React.useRef<SVGSVGElement | null>(null);
    const [hoverRow, setHoverRow] = React.useState(-1);
    const [hoverCol, setHoverCol] = React.useState(-1);

    // compute visual parameters
    const cellSize =
        (props.size.width - (props.columnOrder.length - 1) * props.spacing) /
        props.columnOrder.length;
    const canvasHeight =
        cellSize * (props.columnDoi.length + 1) +
        props.spacing * props.columnDoi.length;

    // construct visual column elements
    const elements: VisAttributes[] = props.columnAttributes
        .flatMap((table, tableId) => {
            return props.columnOrder.map((col, colId) => {
                const idx = table.findIndex(d => d.name === col.name);
                if (idx !== -1) {
                    return {
                        x: (props.spacing + cellSize) * colId,
                        y: (props.spacing + cellSize) * (tableId + 1),
                        width: cellSize,
                        count: props.columnDoi[tableId][idx],
                        columnName: col.name,
                        id: colId + (tableId + 1) * props.columnOrder.length,
                        tableId: tableId,
                    };
                } else {
                    return null;
                }
            });
        })
        .filter(d => d !== null) as VisAttributes[];

    // construct visual cluster elements
    const clusterElements: VisAttributes[] = props.clusterDoi.map(
        (cluster, clusterId) => {
            return {
                x: (props.spacing + cellSize) * cluster.start,
                y: 0,
                width:
                    cluster.count * cellSize +
                    (cluster.count - 1) * props.spacing,
                count: cluster.doi,
                id: clusterId,
                columnName: props.columnOrder
                    .slice(cluster.start, cluster.start + cluster.count)
                    .map(d => d.name),
                tableId: -1,
            };
        }
    );

    const visElements = elements.concat(clusterElements);

    function handleHover(col: number, row: number) {
        if (col === -1) {
            (props.columnHoverCallback as ColumnCallback)([]);
            setHoverCol(-1);
            setHoverRow(-1);
        } else if (row > 0) {
            (props.columnHoverCallback as ColumnCallback)([
                props.columnOrder[col].name,
            ]);
            setHoverCol(col);
            setHoverRow(row);
        } else {
            const cluster = props.clusterDoi.find(
                d => d.start <= col && d.start + d.count > col
            );
            if (cluster !== undefined) {
                (props.columnHoverCallback as ColumnCallback)(
                    props.columnOrder
                        .slice(cluster.start, cluster.start + cluster.count)
                        .map(d => d.name)
                );
                setHoverCol(col);
                setHoverRow(row);
            }
        }
    }

    const tooltipContent = (
        <MapTooltip
            columnOrder={props.columnOrder}
            attributes={props.columnAttributes}
            rowId={hoverRow}
            colId={hoverCol}
            clusters={props.clusterDoi}
        />
    );
    const tooltipOpen = tooltipContent !== null;

    // output
    return (
        <>
            <HoverPopper
                open={tooltipOpen}
                anchorEl={tooltipOpen ? svgRef.current : undefined}
                placement="top"
            >
                {tooltipContent}
            </HoverPopper>
            <DoiMapSvg
                svgRef={svgRef}
                width={props.size.width}
                height={canvasHeight}
                cellSize={cellSize}
                spacing={props.spacing}
                elements={visElements}
                max={props.maxValue}
                min={props.minValue}
                hover={
                    hoverCol !== -1 && hoverRow !== -1
                        ? [hoverCol, hoverRow]
                        : undefined
                }
                elementHoverCallback={
                    props.columnHoverCallback !== undefined
                        ? handleHover
                        : undefined
                }
            />
        </>
    );
});
